import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useUI, type HomeTab } from '../store/ui'
import { useTabs } from '../store/tabs'
import { useActivityState } from '../store/activityState'

const HOME_TABS: { key: HomeTab; label: string }[] = [
  { key: 'feed', label: 'feed' },
  { key: 'discovery', label: 'discovery' },
  { key: 'profile', label: 'profile' },
  { key: 'follows', label: 'follows' },
  { key: 'blocks', label: 'blocks' },
  { key: 'activity', label: '📡 activity' },
  { key: 'ai', label: '✦ AI' },
  { key: 'saved', label: 'saved' },
]

const ROUTE_LINKS: { path: string; icon: string; label: string }[] = [
  { path: '/tabs-search', icon: '⌕', label: 'search tabs' },
  { path: '/schedule', icon: '⧗', label: 'queue' },
  { path: '/failure-log', icon: '⚠', label: 'error log' },
]

/**
 * Rows 2-3 of the portrait top ribbon:
 *   row 2: horizontally scrollable home-tab selector (desktop ExploreBar
 *          parity) · divider · quick route links · pin toggle
 *   row 3: open-tabs strip with the same ◉/◯ activity include-exclude
 *          pills + avatars as the desktop ExploreBar
 * `visible` rolls both rows away via max-height so the 150ms
 * .ribbon-transition can animate collapse/expand and scroll auto-hide.
 */
export function MobileTopRibbon({ visible }: { visible: boolean }) {
  const navigate = useNavigate()
  const loc = useLocation()

  const tab = useUI((s) => s.homeTab)
  const setTab = useUI((s) => s.setHomeTab)
  const setPanel = useUI((s) => s.setPanel)
  const pinned = useUI((s) => s.mobileRibbonPinned)
  const togglePinned = useUI((s) => s.toggleMobileRibbonPinned)

  const tabs = useTabs((s) => s.tabs)
  const closeTab = useTabs((s) => s.close)
  const excludedTabs = useActivityState((s) => s.excludedTabs)
  const toggleActivityTab = useActivityState((s) => s.toggleTab)
  const selectAllActivityTabs = useActivityState((s) => s.selectAllTabs)
  const isIncluded = (handle: string) =>
    !excludedTabs.includes(handle.toLowerCase())

  const profileMatch = /^\/profile\/([^/]+)/.exec(loc.pathname)
  const activeTabHandle = profileMatch ? decodeURIComponent(profileMatch[1]).toLowerCase() : null
  const onHome = loc.pathname === '/'

  function goHomeTab(t: HomeTab) {
    setTab(t)
    setPanel('content')
    if (loc.pathname !== '/') navigate('/')
  }

  return (
    <div
      className={clsx(
        'ribbon-transition overflow-hidden',
        visible ? 'max-h-28 opacity-100' : 'max-h-0 opacity-0',
      )}
    >
      {/* row 2: home tabs (scrollable) · divider · quick routes · pin */}
      <div className="flex items-center border-t border-white/10">
        <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-2 py-1">
          {HOME_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => goHomeTab(t.key)}
              className={clsx(
                'shrink-0 whitespace-nowrap border px-2 py-1 text-[11px]',
                onHome && t.key === tab
                  ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
                  : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-0.5 border-l border-white/10 px-1">
          {ROUTE_LINKS.map((r) => (
            <button
              key={r.path}
              type="button"
              onClick={() => navigate(r.path)}
              title={r.label}
              className={clsx(
                'grid h-7 w-7 place-items-center text-sm',
                loc.pathname === r.path ? 'block-active' : 'text-ink-300 hover:bg-white/5',
              )}
            >
              {r.icon}
            </button>
          ))}
          <button
            type="button"
            onClick={togglePinned}
            title={pinned ? 'unpin — ribbons auto-hide on scroll' : 'pin — keep ribbons open while scrolling'}
            className={clsx(
              'grid h-7 w-7 place-items-center text-sm',
              pinned ? 'block-active' : 'text-ink-300 hover:bg-white/5',
            )}
          >
            {pinned ? '⚑' : '⚐'}
          </button>
        </div>
      </div>

      {/* row 3: open-tabs strip — mirrors the desktop ExploreBar pills */}
      {tabs.length > 0 ? (
        <div className="no-scrollbar flex items-center gap-1 overflow-x-auto border-t border-white/10 px-2 py-1">
          {tabs.map((t) => {
            const isActive = activeTabHandle === t.handle
            const included = isIncluded(t.handle)
            // Three visual states layered onto the same pill:
            //   • route-active (you're on /profile/<handle> right now) — accent border
            //   • activity-included — solid blue treatment
            //   • activity-excluded — dashed border, dimmed, strikethrough handle
            const cls = isActive
              ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
              : included
                ? 'border-sky-400/40 bg-sky-500/30 text-white hover:border-white hover:bg-sky-300/50 hover:text-black'
                : 'border-dashed border-white/15 bg-white/5 text-ink-400 hover:border-white/40 hover:text-ink-100'
            return (
              <span
                key={t.handle}
                className={clsx(
                  'flex shrink-0 items-center gap-1 rounded-full border px-1 py-0.5 text-[11px]',
                  cls,
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleActivityTab(t.handle)}
                  className="rounded-full px-1 text-[12px] leading-none"
                  title={
                    included
                      ? `included in activity — click to exclude @${t.handle}`
                      : `excluded from activity — click to include @${t.handle}`
                  }
                  aria-label={
                    included
                      ? `exclude @${t.handle} from activity`
                      : `include @${t.handle} in activity`
                  }
                >
                  {included ? '◉' : '◯'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/profile/${encodeURIComponent(t.handle)}`)}
                  className="flex items-center gap-1"
                  title={`open @${t.handle}`}
                >
                  {t.avatar ? (
                    <img
                      src={t.avatar}
                      alt=""
                      className="h-4 w-4 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : null}
                  <span
                    className={clsx(
                      'max-w-[96px] truncate',
                      !included && 'line-through',
                    )}
                  >
                    @{t.handle}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeTab(t.handle)
                    if (isActive) navigate('/')
                  }}
                  className="rounded text-ink-400 hover:text-rose-300"
                  title="close tab"
                  aria-label={`close @${t.handle}`}
                >
                  ✕
                </button>
              </span>
            )
          })}
          {excludedTabs.length > 0 ? (
            <button
              type="button"
              onClick={() => selectAllActivityTabs()}
              className="shrink-0 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-ink-200 hover:bg-white/10"
              title="re-include every tab in activity"
            >
              ✓ all
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
